import React, {CSSProperties, useCallback, useContext, useEffect, useMemo, useRef, useState,} from "react";
import cx from 'clsx';
import {useMotionValue, useViewportScroll} from "framer-motion";

import {Link} from "@montana/shared/link";
import {Container} from "@montana/shared/container";
import {MiniCart} from "@montana/shared/mini-cart";
import {usePage} from '@montana/containers/page';
import {
    CartPageKind,
    CheckoutB2CPageKind,
    LoginPageKind,
    ReceiptPageKind,
    ShippingPageKind
} from "@montana/pages/dynamic-page.component";
import {
    DesktopAndLarger,
    MobileAndTabletLandscape,
    Responsive,
    ScreenSizes,
    TabletAndLarger
} from "@montana/shared/responsive";
import {useDebouncedMemo} from "@montana/shared/use-debounced-memo";
import {Search} from "@montana/shared/search";

import {FirstRenderContext} from "@montana/context/first-render.context";
import {DesktopMenu} from "./desktop-menu";
import {MobileMenuToggle} from "./mobile-menu/mobile-menu-toggle.component";
import {HeaderLogo} from "./header-logo";
import {HeaderLogoSizes} from "./header-logo/header-logo.component";
import {UspMenu} from "./usp-menu/usp-menu.component";
import {useWindowSize} from "react-use";
import {HeaderSearch} from "./header-search";
import {SvgIcon} from "@montana/shared/svg-icon";
import {useCart} from "@montana/containers/cart";
import {NoticeBar} from "./notice-bar/notice-bar.component";
import {useSiteStructure} from "@montana/containers/site-structure";
import {getTextColorFor} from '@montana/helpers/theme-color.helper';
import {TEXT_LIGHT} from "@montana/constants/colors";
import {useSettings} from "@montana/containers/settings/settings.container";
import {CUSTOMER_MODE} from "@montana/constants/customer-mode";
import {useDynamicSettings} from "@montana/containers/dynamic-settings";
import {SidePanel} from "@montana/shared/side-panel/side-panel.component";
import {Drawer} from "@montana/shared/drawer";
import {LayoutContext} from "@montana/context/layout.context";
import {DrawerMenu} from "@montana/shell/header/drawer-menu/drawer-menu";
import {useMediaQuery} from "@montana/hooks/useMediaQuery";
import {useRouter} from "@montana/containers/router";
import {IMainMenu, ILevel1MenuItem, ILevel2MenuItem, ILevel3MenuItem} from "@montana/models/generated/cms.interface";

export interface IHeaderProps {
    scrollbackTreshold?: number;
}

type TMenuMatchPath = {
    level1Item: ILevel1MenuItem;
    level2Item?: ILevel2MenuItem;
    level3Item?: ILevel3MenuItem;
};

function normalizeMenuUrl(url?: string): string | undefined {
    if (!url) {
        return undefined;
    }

    const normalizedUrl = url.split(/[?#]/)[0];

    if (!normalizedUrl) {
        return undefined;
    }

    return normalizedUrl !== "/" ? normalizedUrl.replace(/\/+$/, "") : normalizedUrl;
}

function isMatchingRoutePath(routePath?: string, itemUrl?: string): boolean {
    const normalizedRoutePath = normalizeMenuUrl(routePath);
    const normalizedItemUrl = normalizeMenuUrl(itemUrl);

    if (!normalizedRoutePath || !normalizedItemUrl) {
        return false;
    }

    return normalizedRoutePath === normalizedItemUrl || normalizedRoutePath.startsWith(`${normalizedItemUrl}/`);
}

function getMenuItemIdWithSubItemsForRoute(
    mainMenuItems: IMainMenu["mainMenuItems"] = [],
    routeCandidates: string[] = []
): string | undefined {
    const normalizedCandidates = Array.from(new Set(routeCandidates.map(normalizeMenuUrl).filter(Boolean) as string[]));

    if (!normalizedCandidates.length) {
        return undefined;
    }

    let bestMatch: TMenuMatchPath | undefined;
    let bestMatchLength = -1;

    const updateBestMatch = (matchPath: TMenuMatchPath, matchedUrl?: string) => {
        const normalizedMatchedUrl = normalizeMenuUrl(matchedUrl);

        if (!normalizedMatchedUrl) {
            return;
        }

        if (normalizedMatchedUrl.length > bestMatchLength) {
            bestMatch = matchPath;
            bestMatchLength = normalizedMatchedUrl.length;
        }
    };

    for (const routeCandidate of normalizedCandidates) {
        for (const level1Item of mainMenuItems) {
            if (isMatchingRoutePath(routeCandidate, level1Item.link?.url)) {
                updateBestMatch({level1Item}, level1Item.link?.url);
            }

            for (const level2Item of level1Item.subItems || []) {
                if (isMatchingRoutePath(routeCandidate, level2Item.link?.url)) {
                    updateBestMatch({level1Item, level2Item}, level2Item.link?.url);
                }

                for (const level3Item of level2Item.subItems || []) {
                    if (isMatchingRoutePath(routeCandidate, level3Item.link?.url)) {
                        updateBestMatch({level1Item, level2Item, level3Item}, level3Item.link?.url);
                    }
                }
            }
        }
    }

    if (!bestMatch) {
        return undefined;
    }

    const level1HasSubItems = Boolean(bestMatch.level1Item.subItems?.length);
    const level2HasSubItems = Boolean(bestMatch.level2Item?.subItems?.length);

    if (bestMatch.level3Item) {
        return level2HasSubItems ? bestMatch.level2Item?.itemId : (level1HasSubItems ? bestMatch.level1Item.itemId : undefined);
    }

    if (bestMatch.level2Item) {
        return level2HasSubItems ? bestMatch.level2Item.itemId : (level1HasSubItems ? bestMatch.level1Item.itemId : undefined);
    }

    return level1HasSubItems ? bestMatch.level1Item.itemId : undefined;
}

const Header: React.FC<IHeaderProps> = React.memo(({scrollbackTreshold = 20}: IHeaderProps): JSX.Element => {

    const {kind, pageTheme, hasHero, isTransitioning, breadcrumb} = usePage();
    const {usPs, mainMenuItems, mainMenuFooterLinks, secondaryMainMenuItems} = useSiteStructure();
    const {lines, accumulatedQuantity, deleteBuffer} = useCart();
    const {links} = useDynamicSettings();
    const {customerMode, market} = useSettings();
    const {isRouting, location} = useRouter();

    const [isContractorContext, setIsContractorContext] = useState(customerMode === CUSTOMER_MODE.PROFESSIONAL);

    useEffect(() => {
        // Set contractor context
        if (customerMode) {
            setIsContractorContext(customerMode === CUSTOMER_MODE.PROFESSIONAL);
        }
    }, [customerMode]);

    const isFirstRender = useContext(FirstRenderContext);

    const isMobileOrTablet = useMediaQuery(`(max-width: ${ScreenSizes.TabletLandscape - 1}px)`);

    const {height: windowHeight} = useWindowSize();

    const [darkTheme, setDarkTheme] = useState(true);
    const [headerTopPos, setHeaderTopPos] = useState<number>(0);
    const showUspBar = useMemo<boolean>(
        () => !usPs ? true : usPs.length > 0,
        [usPs]
    );

    const [showNoticeBar, setShowNoticeBar] = useState(true);

    const headerRef = useRef<HTMLDivElement | null>(null);

    const headerLogoSize = useMemo((): HeaderLogoSizes => {
        if (!kind) {
            return HeaderLogoSizes.Normal;
        } else {
            return kind === LoginPageKind ? HeaderLogoSizes.Large : HeaderLogoSizes.Normal;
        }
    }, [kind]);

    const [mainmenuLeftPos, setMainmenuLeftPos] = useState<number>(300);
    const handleLeftPosChange = useCallback((pos: number) => {
        setMainmenuLeftPos(Math.ceil(pos));
    }, []);

    // Search overlay or mobile menu etc, not mega menu

    const [drawerMenuState, setdrawerMenuState] = useState<boolean>(false);
    const [searchActive, setSearchActive] = useState(false);
    const activeOverlay = useMemo(() => (isMobileOrTablet && searchActive) || drawerMenuState, [searchActive, isMobileOrTablet, drawerMenuState]);
    const {scrollY} = useViewportScroll();

    const scrollDirection = useMotionValue(1);
    const scrollDirectionChangedPos = useMotionValue(0);


    const searchClose = useCallback(() => {
        setSearchActive(false);
    }, []);

    const searchOpen = useCallback(() => {
        setHeaderTopPos(scrollY.get());
        setSearchActive(true);
        setdrawerMenuState(false);
    }, []);

    const toggleDrawerMenu = useCallback(() => {
        setHeaderTopPos(scrollY.get());
        setdrawerMenuState(prev => {
            const nextState = !prev;
            if (nextState && searchActive) {
                searchClose();
            }
            return nextState;
        });
    }, [searchActive, searchClose]);

    const [selectedDrawerMenuItemId, setSelectedDrawerMenuItemId] = useState<string | undefined>();

    const [isBelowStickyStart, setIsBelowStickyStart] = useState(false);
    const [isAboveFold, setIsAboveFold] = useState(true);
    const [isScrollingBack, setIsScrollingBack] = useState(false);

    const debouncedIsScrollingBack = useDebouncedMemo(
        () => isScrollingBack,
        [isScrollingBack],
        500,
        {leading: true}
    );

    const displayHeaderNavigation = useMemo(() => {
        return kind !== CartPageKind && kind !== ShippingPageKind && kind !== CheckoutB2CPageKind && kind !== ReceiptPageKind;
    }, [kind]);

    useEffect(() => {
        if (!isRouting) {
            const routeCandidates = [
                location.pathname,
                ...(breadcrumb?.map(item => item.url).filter((url): url is string => !!url).reverse() || [])
            ];
            const foundItemId = getMenuItemIdWithSubItemsForRoute(mainMenuItems, routeCandidates);

            if (foundItemId) {
                setSelectedDrawerMenuItemId(foundItemId);
            } else if (isMobileOrTablet) {
                setSelectedDrawerMenuItemId(mainMenuItems?.[0]?.itemId);
            } else {
                setSelectedDrawerMenuItemId(undefined);
            }
        }
    }, [location.pathname, isRouting, breadcrumb, kind, isTransitioning, isMobileOrTablet]);

    // We want to show the header when the user is at the top few pixels (depending on
    // scroll direction) of the page. Albeit in a different styling. headerBgActive makes
    // the difference in regards to background/no background.
    const [scrollIsAtTop, setScrollIsAtTop] = useState(true);

    const {isLanguageSelectorOpen} = useContext(LayoutContext);

    const headerExpanded = useMemo(
        () => !isFirstRender && isAboveFold
            ? !isBelowStickyStart || debouncedIsScrollingBack
            : debouncedIsScrollingBack,
        [isFirstRender, isBelowStickyStart, isAboveFold, debouncedIsScrollingBack]
    );

    const headerBgActive = useMemo(
        () => {
            if (isTransitioning) {
                return false;
            }
            if (activeOverlay) {
                return true;
            }
            if (isMobileOrTablet) {
                return !scrollIsAtTop;
            }
            return debouncedIsScrollingBack && !scrollIsAtTop;
        },
        [isTransitioning, activeOverlay, isMobileOrTablet, scrollIsAtTop, debouncedIsScrollingBack]
    );

    const invertText = useMemo(() => {
        if (activeOverlay || headerBgActive) {
            return false;
        }
        return darkTheme;
    }, [darkTheme, activeOverlay, headerBgActive]);

    useEffect(() => {

        const unsubscribe = scrollY.onChange((value) => {
            const velocity = scrollY.getVelocity();
            const direction = (velocity >= 0) ? 1 : -1;
            const aboveFold = value < windowHeight * 0.5;
            const scrollingFast = (direction === 1)
                ? velocity > 200
                : velocity < -1000;

            setIsAboveFold(aboveFold);
            setIsBelowStickyStart(value > 200);
            // setIsPastHeader(value > 200 && value < windowHeight * 0.5);
            setScrollIsAtTop(hasHero
                ? value <= 50
                : value <= (scrollDirection.get() > 0 ? 50 : 20));

            // Only change header collapse/expand state if scroll speed (velocity) is high
            // or if we're above the fold
            if (direction !== scrollDirection.get() && (scrollingFast || aboveFold)) {
                scrollDirection.set(direction);
                // Set the scrollY position at which scroll direction changed
                scrollDirectionChangedPos.set(value);
            }

            // We want to show the header when 1) the user has scrolled more upwards than the
            // scrollbackThreshold and 2) the point at which they start scrolling up is below
            // the first 75vh of the page. This last part saves us from a bit of weird UX when
            // scrolling within the top area of the page.
            if (value > 0) {
                setIsScrollingBack(scrollDirectionChangedPos.get() - value >= scrollbackTreshold && scrollDirectionChangedPos.get() > windowHeight * 0.75);
            } else {
                setIsScrollingBack(false);
            }
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!pageTheme) {
            setDarkTheme(false);
        } else {
            setDarkTheme(getTextColorFor(pageTheme) === TEXT_LIGHT);
        }
    }, [pageTheme]);

    useEffect(() => {
        if (!scrollIsAtTop) {
            setIsScrollingBack(true);
        }
    }, [lines]);

    useEffect(() => {
        setdrawerMenuState(false);
        searchClose();
    }, [isTransitioning, customerMode]);

    const showMiniCart = useMemo(() => {
        // always show the mini cart on private and on professional only if the cart already has products
        return !market?.disableCart && (!isContractorContext || accumulatedQuantity > 0 && deleteBuffer?.length !== accumulatedQuantity);
    }, [market, isContractorContext, accumulatedQuantity, deleteBuffer]);

    function handleDrawerMenuStateChange(state: boolean) {
        setdrawerMenuState(state);
        if (searchActive) {
            searchClose();
        }
    }

    function handleSearchDrawerOpenChange() {
        searchClose();
        if (drawerMenuState) {
            setdrawerMenuState(false);
        }

    }

    return (
        <>
            <aside className="header__secondary">
                <div className="header__notice">
                    <NoticeBar hide={!showNoticeBar} onClose={() => setShowNoticeBar(false)}/>
                </div>
                <TabletAndLarger>
                    <div className={cx('header__usp-menu', {
                        'header__usp-menu--hidden': showNoticeBar,
                    })}>
                        <UspMenu hide={!showUspBar}/>
                    </div>
                </TabletAndLarger>
            </aside>
            <header data-react-aria-top-layer className={cx('app__header', {
                'app__header--has-usp': showUspBar,
                'app__header--has-notice': showNoticeBar,
                'app__header--has-usp-and-notice': showUspBar && showNoticeBar,
                'app__header--no-animation': !scrollIsAtTop && !isBelowStickyStart && !debouncedIsScrollingBack,
                'app__header--sticky': !isAboveFold || debouncedIsScrollingBack,
            })}
                    style={{'--header-sticky-top-pos': drawerMenuState || searchActive ? `${headerTopPos}px` : 0} as CSSProperties}
            >
                <div
                    ref={headerRef}
                    className={cx('header', {
                        'header--on-dark': invertText && !isLanguageSelectorOpen,
                        'header--expanded': headerExpanded,
                        'header--collapsed': !headerExpanded,
                        'header--active-overlay': activeOverlay,
                        'header--bg-active': headerBgActive
                    })}
                >
                    <div className="header__main">
                        <div
                            className={`header__content${drawerMenuState || searchActive ? ' header__content--overlay' : ''}`}>

                            <Container className="header__container">

                                <nav className="header__content-left" aria-label="primary">
                                    <MobileAndTabletLandscape includeSSR>
                                        <MobileMenuToggle onToggleCallback={toggleDrawerMenu}
                                                          isOpen={drawerMenuState}
                                                          invertText={invertText}/>
                                    </MobileAndTabletLandscape><Responsive maxWidth={500}>
                                    <div className="header__search-toggle">
                                        <HeaderSearch onClose={searchClose} onOpen={searchOpen}
                                                      active={searchActive}/>
                                    </div>
                                </Responsive><DesktopAndLarger>
                                    <DesktopMenu
                                        selectedItemId={selectedDrawerMenuItemId}
                                        onSetSelectedDrawerMenuItemId={setSelectedDrawerMenuItemId}
                                        onToggleDrawerMenu={toggleDrawerMenu}
                                        onLeftPosChange={handleLeftPosChange}
                                        menuItems={mainMenuItems}/>
                                </DesktopAndLarger>
                                </nav>

                                <div className="header__content-mid">
                                    <Link to={isContractorContext ? links?.contractorFrontPage : links?.homePage}
                                          title="Go to frontpage" className="header__logo-link">
                                        {headerLogoSize && <HeaderLogo size={headerLogoSize}/>}
                                    </Link>
                                </div>

                                <div className="header__content-right">
                                    {displayHeaderNavigation && (
                                        <><DesktopAndLarger>
                                            <nav className="header__secondary-menu">
                                                {secondaryMainMenuItems?.map((menu) => (
                                                    <Link
                                                        key={menu.itemId}
                                                        to={menu.link?.url}
                                                        withStroke
                                                        inverseStrokeAnimation
                                                        title={menu.link?.title}
                                                        className="header__customize-link"
                                                    >
                                                        {menu.link?.name}
                                                    </Link>))}
                                            </nav>
                                        </DesktopAndLarger>
                                            <Link to={links?.storeLocatorPage}
                                                  className="header__search-toggle header__content-right-item">
                                                <SvgIcon iconName="place" className="header__place"/>
                                            </Link>
                                            <Responsive minWidth={501}>
                                                <div className="header__search-toggle header__content-right-item">
                                                    <HeaderSearch onClose={searchClose} onOpen={searchOpen}
                                                                  active={searchActive}/>
                                                </div>
                                            </Responsive></>
                                    )}
                                    {showMiniCart && displayHeaderNavigation && (
                                        <div className={cx('header__mini-cart')}>
                                            <MiniCart timeout={5000}/>
                                        </div>
                                    )}

                                </div>
                            </Container>
                        </div>
                    </div>
                </div>
            </header>

            <Drawer
                isInstant={true}
                side="top"
                style={{'--offset-top': isMobileOrTablet ? 'var(--header-height)' : 'auto'} as React.CSSProperties}
                isDismissable={true}
                isOpen={searchActive && isMobileOrTablet}
                onOpenChange={handleSearchDrawerOpenChange}
            >
                <Search onClick={searchClose}/>
            </Drawer>
            <DrawerMenu
                isInstant={true}
                style={{
                    '--offset-top': isMobileOrTablet ? 'var(--header-height)' : 'auto',
                    '--drawer-left-pos': `${mainmenuLeftPos}px`
                } as React.CSSProperties}
                menuContent={{mainMenuItems, mainMenuFooterLinks} as Omit<IMainMenu, 'footer'>}
                selectedItemId={selectedDrawerMenuItemId}
                side="left"
                isDismissable={true}
                isOpen={drawerMenuState}
                onOpenChange={handleDrawerMenuStateChange}
            />
            <SidePanel open={!isMobileOrTablet && searchActive} onDismiss={searchClose}>
                <Search onClick={searchClose}/>
            </SidePanel>
        </>);
});
export default Header

Header.displayName = 'Header';
